import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { ObjectReference } from '~/client/src/shared/components/ObjectReference/ObjectReference'
import { StackedReference } from '~/client/src/shared/components/ObjectReference/StackedReference'
import { IResourceChipItem } from '~/client/src/shared/components/ResourceChipList'
import useWorkflowsByIds from '~/client/src/shared/hooks/useWorkflowsByIds'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'

import WorkflowTypeIcon from '../../WorkflowTypeIcon/WorkflowTypeIcon'
import { ISharedProps } from '../BaseStruxhubInput'
import StruxhubResourceSelector from './StruxhubResourceSelector'

// localization: translated

interface IProps extends ISharedProps {
  workflowIds: string[]
  newWorkflows?: Workflow[]
  isMultiple?: boolean
  onClick?: () => void
  onSelectExisting?: () => void
  onRemove?: (workflow: Workflow) => void
  isViewMode?: boolean
  withAllDrafts?: boolean
  allowSelectExisting?: boolean
  allowCreateNew?: boolean

  workflowTypesStore?: WorkflowTypesStore
  eventsStore?: EventsStore
}

const StruxhubLinkedWorkflowSelector: React.FC<IProps> = ({
  workflowIds,
  newWorkflows = [],
  isMultiple = true,
  onClick,
  onSelectExisting,
  onRemove,
  isViewMode,
  withAllDrafts = false,
  allowSelectExisting = true,
  allowCreateNew = true,
  workflowTypesStore,
  eventsStore,
  isReadOnly,
  isDisabled,
  ...inputProps
}: IProps) => {
  const projectId = eventsStore?.appState.activeProject.id
  const { workflows: fetchedWorkflows, workflowLoading } = useWorkflowsByIds(
    projectId,
    workflowIds,
    withAllDrafts,
  )

  // Retain the last non-empty result so chips don't flicker while the query
  // re-fetches after a chip delete re-keys it.
  const lastResolvedWorkflows = React.useRef<Workflow[]>([])
  if (fetchedWorkflows.length) {
    lastResolvedWorkflows.current = fetchedWorkflows
  }

  const workflows = React.useMemo(() => {
    let fetched = fetchedWorkflows
    if (!fetched.length && workflowLoading) {
      const allowedIds = new Set(workflowIds)
      fetched = lastResolvedWorkflows.current.filter(workflow =>
        allowedIds.has(workflow.id),
      )
    }
    return [...fetched, ...newWorkflows]
  }, [fetchedWorkflows, workflowLoading, workflowIds, newWorkflows])

  const placeholderText = React.useMemo(() => {
    if (allowSelectExisting && allowCreateNew) {
      return `${Localization.translator.select} ${Localization.translator.or} ${Localization.translator.createNew}`
    }
    if (allowCreateNew && !allowSelectExisting) {
      return Localization.translator.createNew
    }
    return Localization.translator.select
  }, [allowSelectExisting, allowCreateNew])

  // Unsaved workflows have no id yet, so they key by position.
  const workflowByChipId = React.useMemo(
    () =>
      new Map(
        workflows.map((workflow, index) => [
          workflow.id || `new-workflow-${index}`,
          workflow,
        ]),
      ),
    [workflows],
  )

  // Built in render, not memoized: captions and deleted types are observable store reads.
  const items: IResourceChipItem[] = [...workflowByChipId].map(
    ([id, workflow]) => ({
      id,
      label: workflow.getCaption(workflowTypesStore),
      isDeleted: !!workflowTypesStore?.getPermitTypeById(workflow.typeId)
        ?.isDeleted,
    }),
  )

  const renderIcon = React.useCallback(
    (item: IResourceChipItem) => {
      const workflow = workflowByChipId.get(item.id)
      const permitType = workflowTypesStore?.getPermitTypeById(workflow?.typeId)
      return (
        permitType && (
          <WorkflowTypeIcon
            iconName={permitType.iconName}
            className="tw:ml-0.5"
            size="small"
          />
        )
      )
    },
    [workflowByChipId, workflowTypesStore],
  )

  const handleRemove = React.useCallback(
    (id: string) => onRemove?.(workflowByChipId.get(id)),
    [onRemove, workflowByChipId],
  )

  // A single workflow reads like a single location: its glyph and title, its code beneath.
  const single = !isMultiple && items.length === 1 && !items[0].isDeleted
  const singleWorkflow = single ? workflowByChipId.get(items[0].id) : null
  const singlePermitType = singleWorkflow
    ? workflowTypesStore?.getPermitTypeById(singleWorkflow.typeId)
    : null
  const valueContent = singleWorkflow && (
    <StackedReference
      size="dense"
      secondLine={singleWorkflow.code}
      reference={
        <ObjectReference
          icon={
            singlePermitType && (
              <WorkflowTypeIcon
                iconName={singlePermitType.iconName}
                size="small"
              />
            )
          }
          variant="plain"
          size="dense"
        >
          <span>{singleWorkflow.getCaption(workflowTypesStore, true)}</span>
        </ObjectReference>
      }
    />
  )

  return (
    <StruxhubResourceSelector
      {...inputProps}
      isReadOnly={isReadOnly}
      isDisabled={isDisabled}
      items={items}
      valueContent={valueContent}
      isSingleValue={!isMultiple}
      placeholderText={placeholderText}
      allowSelectExisting={allowSelectExisting}
      onClick={onClick}
      onSelectExisting={onSelectExisting}
      onRemove={isMultiple ? handleRemove : undefined}
      renderIcon={renderIcon}
      isViewMode={isViewMode}
    />
  )
}

export default inject(
  'eventsStore',
  'workflowTypesStore',
)(observer(StruxhubLinkedWorkflowSelector))
